import { environment } from '../../environments/environment';

let loadPromise: Promise<typeof google> | null = null;

const SCRIPT_ID = 'google-maps-js-api';
const LIBRARIES = ['places', 'geometry', 'marker'].join(',');
const VERSION = '3.63';

/**
 * Load the Google Maps JS API once per page. Subsequent calls return the
 * same promise so the script is never injected twice. Resolves with the
 * `google` namespace once `google.maps` is fully ready.
 */
export function loadGoogleMaps(): Promise<typeof google> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps can only load in the browser.'));
  }

  if ((window as any).google?.maps) {
    return Promise.resolve((window as any).google);
  }

  if (loadPromise) return loadPromise;

  const key = environment.mapsApiKey;
  if (!key) {
    return Promise.reject(new Error('mapsApiKey is missing from environment.'));
  }

  loadPromise = new Promise<typeof google>((resolve, reject) => {
    const callbackName = '__gmapsReady_' + Math.random().toString(36).slice(2);
    (window as any)[callbackName] = () => {
      delete (window as any)[callbackName];
      resolve((window as any).google);
    };

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src =
      'https://maps.googleapis.com/maps/api/js' +
      `?key=${encodeURIComponent(key)}` +
      `&libraries=${LIBRARIES}` +
      `&v=${VERSION}` +
      `&loading=async` +
      `&callback=${callbackName}`;
    script.onerror = () => {
      loadPromise = null;
      delete (window as any)[callbackName];
      reject(new Error('Failed to load Google Maps script.'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}
